/* Port du filtre K9 FullStack : contrôles source isolés, orchestrateur raccordé à Trade. */
class FiltersChangedEvent extends CustomEvent {
  constructor(filter_params, sorting_params) {
    super('k9-fullstack-filters-changed', { bubbles: true, detail: { filter_params, sorting_params } });
  }
}

// The FullStack controls below keep their markup and change events. One owning
// controller replaces the Trade grid, synchronizes desktop/mobile and leaves
// the collection banner, comparison, FAQ, apps and cart outside the update.
class K9FullStackCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-k9-open]');
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    this.filterParams = this.querySelector('k9-fullstack-filter-component')?.getFiltersParams() || '';
    this.sortingParams = this.querySelector('k9-fullstack-sort-component')?.getSortingParams() || '';
    this.addEventListener('k9-fullstack-filters-changed', this.onChanged, { signal });
    this.section.addEventListener('click', this.onLink, { signal });
    this.addEventListener('submit', this.onSubmit, { signal });
    window.addEventListener('popstate', this.onPopState, { signal });
    this.opener?.addEventListener('click', this.open, { signal });
    this.querySelector('[data-k9-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog?.addEventListener('close', this.onClose, { signal });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, { signal });
    matchMedia('(min-width:750px)').addEventListener('change', (event) => {
      if (event.matches && this.dialog?.open) this.dialog.close();
    }, { signal });
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.listeners?.abort();
    this.request?.abort();
  }

  getCurrentFiltersParams() { return this.filterParams; }
  getCurrentSortingParams() { return this.sortingParams; }

  open = () => {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog.showModal();
    this.opener.setAttribute('aria-expanded', 'true');
  };

  onClose = () => {
    document.body.style.overflow = this.previousOverflow ?? '';
    this.opener.setAttribute('aria-expanded', 'false');
    this.opener.focus({ preventScroll: true });
  };

  onChanged = (event) => {
    event.stopPropagation();
    this.filterParams = event.detail.filter_params;
    this.sortingParams = event.detail.sorting_params;
    const url = new URL(location.href);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page' || key === 'section_id') url.searchParams.delete(key);
    }
    for (const [key, value] of new URLSearchParams(this.filterParams)) url.searchParams.append(key, value);
    for (const [key, value] of new URLSearchParams(this.sortingParams)) url.searchParams.set(key, value);
    this.render(url, true);
  };

  onSubmit = (event) => {
    const filter = event.target.closest('k9-fullstack-filter-component');
    if (!filter) return;
    event.preventDefault();
    this.dispatchEvent(new FiltersChangedEvent(filter.getFiltersParams(), this.sortingParams));
  };

  onLink = (event) => {
    const link = event.target.closest('.pagination a[href], .k9-fullstack-active a[href]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const url = new URL(link.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    event.preventDefault();
    this.render(url, true, Boolean(link.closest('.pagination')));
  };

  onPopState = () => this.render(new URL(location.href), false);

  async render(url, addHistory, scrollToGrid = false) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : '';
    this.setAttribute('aria-busy', 'true');
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', this.section.id.replace('shopify-section-', ''));
    try {
      const response = await fetch(requestUrl, { signal: request.signal });
      if (!response.ok) throw new Error('Collection response unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request.signal.aborted) return;
      const products = parsed.querySelector('[data-ref="k9-main-collection-products"]');
      const current = this.section.querySelector('[data-ref="k9-main-collection-products"]');
      if (!products || !current) throw new Error('Collection grid unavailable');
      const count = products.querySelector('[data-ref="results-count"]')?.textContent;
      // Same cancellation used by Trade's native AJAX renderer: newly inserted
      // cards remain visible without waiting for an observer created at page load.
      products.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
      current.replaceWith(products);
      this.querySelectorAll('k9-fullstack-filter-component').forEach((filter) => filter.update(parsed));
      const selectedSort = url.searchParams.get('sort_by') || this.dataset.defaultSort;
      this.querySelectorAll('[data-ref="sorting-select"]').forEach((select) => { select.value = selectedSort; });
      this.filterParams = this.querySelector('k9-fullstack-filter-component')?.getFiltersParams() || '';
      this.sortingParams = this.querySelector('k9-fullstack-sort-component')?.getSortingParams() || '';
      if (count) this.querySelector('[data-k9-mobile-count]').textContent = count;
      if (addHistory) history.pushState({}, '', url.pathname + url.search);
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      if (scrollToGrid) {
        const top = products.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      }
      this.dispatchEvent(new CustomEvent('k9-fullstack-filters-updated', { bubbles: true }));
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(url.pathname + url.search);
    } finally {
      if (!request.signal.aborted) this.removeAttribute('aria-busy');
    }
  }
}

class FilterComponent extends HTMLElement {
  constructor() {
    super();

    this.form = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-ref="filters-form"]');

    this.addEventListener('change', this.#onFiltersChanged);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onFiltersChanged);
  }

  #onFiltersChanged = (event) => {
    if (event.target.type === 'range') return;
    event.preventDefault();

    const filterParams = this.getFiltersParams();
    const sortingParams = this.closest('k9-fullstack-collection-filters').getCurrentSortingParams();

    this.closest('k9-fullstack-collection-filters').dispatchEvent(new FiltersChangedEvent(filterParams, sortingParams));
  };

  getFiltersParams() {
    const formData = new FormData(this.form);
    const newParameters = new URLSearchParams(formData);

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
      if (newParameters.has(key)) newParameters.set(key, newParameters.get(key).replace(',', '.'));
    }
    newParameters.delete('page');

    return newParameters.toString();
  }

  renderFilters(newFiltersHtml) {
    this.innerHTML = newFiltersHtml.innerHTML;
    this.form = this.querySelector('[data-ref="filters-form"]');
  }

  // K9-START: sidebar-filter-update — surgical sidebar refresh + active-filters/results-count sync
  // Single entry point the AJAX orchestrator (<k9-fullstack-collection-filters>) calls after
  // fetching the new collection HTML. Refreshes the filter UI surgically (sidebar) or via
  // the native full swap (other modes), then refreshes the section-level auxiliary UI
  // (active filter tags + results count). Returns true when a matching filter block was found.
  //
  // @param {Document} fetchedDoc - parsed document from the AJAX response
  update(fetchedDoc) {
    if (!fetchedDoc) return false;

    const ref = this.getAttribute('data-ref');
    const newFiltersHtml = ref ? fetchedDoc.querySelector(`[data-ref="${ref}"]`) : null;

    if (newFiltersHtml) {
      if (ref === 'filters-sidebar' || ref === 'filters-modal') {
        this.renderSidebarFilters(newFiltersHtml);
      } else {
        this.renderFilters(newFiltersHtml);
      }
    }

    this.refreshAuxiliary(fetchedDoc);

    return Boolean(newFiltersHtml);
  }

  // Sidebar display mode: surgically replace the inner content of each
  // data-ref="filter-raw" group instead of swapping the whole component.
  // This preserves accordion open/closed state, scroll position and focus
  // (a full innerHTML swap via renderFilters would reset all of them).
  renderSidebarFilters(newFiltersHtml) {
    const currentFilters = this.querySelectorAll('[data-ref="filter-raw"]');
    const newFilters = newFiltersHtml.querySelectorAll('[data-ref="filter-raw"]');

    currentFilters.forEach((currentFilter, index) => {
      const nextFilter = newFilters[index];
      if (nextFilter) currentFilter.innerHTML = nextFilter.innerHTML;
    });
  }

  // Refresh the section-level auxiliary UI from the fetched collection HTML:
  //   - data-ref="active-filters-container" : active filter tags + "En stock" toggle state
  //   - data-ref="results-count"            : the products count line
  // Both nodes live OUTSIDE <k9-fullstack-filter-component> (inside ProductGridContainer in the
  // section), so we resolve them against the owning .shopify-section. Whole-node replace
  // is safe because removal clicks are delegated at the section level by the orchestrator.
  refreshAuxiliary(fetchedDoc) {
    const section = this.closest('.shopify-section');
    if (!section || !fetchedDoc) return;

    const newActiveFilters = fetchedDoc.querySelector('[data-ref="active-filters-container"]');
    const currentActiveFilters = section.querySelector('[data-ref="active-filters-container"]');
    if (newActiveFilters && currentActiveFilters) {
      currentActiveFilters.replaceWith(newActiveFilters);
    }

    const newCount = fetchedDoc.querySelector('[data-ref="results-count"]');
    const currentCount = section.querySelector('[data-ref="results-count"]');
    if (newCount && currentCount) {
      currentCount.replaceWith(newCount);
    }
  }
  // K9-END: sidebar-filter-update
}

if (!customElements.get('k9-fullstack-filter-component')) {
  customElements.define('k9-fullstack-filter-component', FilterComponent);
}

class SortComponent extends HTMLElement {
  constructor() {
    super();

    this.sortingSelect = null;
  }

  connectedCallback() {
    this.sortingSelect = this.querySelector('[data-ref="sorting-select"]');

    this.sortingSelect.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.sortingSelect.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const sortOptionValue = event.target.value;

    if (sortOptionValue) {
      const filterParams = this.closest('k9-fullstack-collection-filters').getCurrentFiltersParams();
      const sortingParams = this.getSortingParams();

      this.closest('k9-fullstack-collection-filters').dispatchEvent(new FiltersChangedEvent(filterParams, sortingParams));
    }
  };

  getSortingParams() {
    return `sort_by=${this.sortingSelect.value}`;
  }
}

if (!customElements.get('k9-fullstack-sort-component')) {
  customElements.define('k9-fullstack-sort-component', SortComponent);
}

class K9RangeSlider extends HTMLElement {
  connectedCallback() {
    this.rangeMin = this.querySelector('[data-ref="range-min"]');
    this.rangeMax = this.querySelector('[data-ref="range-max"]');
    this.trackFill = this.querySelector('[data-ref="track-fill"]');

    const textInputs = this.querySelectorAll('.filter-price-range-inputs__field input[type="text"]');
    this.inputMin = textInputs[0];
    this.inputMax = textInputs[1];

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) return;

    this.rangeMin.addEventListener('input', this.#onSliderInput);
    this.rangeMax.addEventListener('input', this.#onSliderInput);




    this.rangeMin.addEventListener('change', this.#onSliderCommit);
    this.rangeMax.addEventListener('change', this.#onSliderCommit);
    this.inputMin.addEventListener('change', this.#onTextChange);
    this.inputMax.addEventListener('change', this.#onTextChange);

    this.#updateTrack();
  }

  disconnectedCallback() {
    if (!this.rangeMin) return;
    this.rangeMin.removeEventListener('input', this.#onSliderInput);
    this.rangeMax.removeEventListener('input', this.#onSliderInput);




    this.rangeMin.removeEventListener('change', this.#onSliderCommit);
    this.rangeMax.removeEventListener('change', this.#onSliderCommit);
    this.inputMin.removeEventListener('change', this.#onTextChange);
    this.inputMax.removeEventListener('change', this.#onTextChange);
  }

  /** Parse a number that may use comma as decimal separator (French locale) */
  #parse(val) {
    return parseFloat(String(val).replace(',', '.')) || 0;
  }

  #onSliderInput = (event) => {
    let minVal = this.#parse(this.rangeMin.value);
    let maxVal = this.#parse(this.rangeMax.value);

    if (minVal > maxVal) {
      if (event.target === this.rangeMin) {
        this.rangeMin.value = maxVal;
        minVal = maxVal;
      } else {
        this.rangeMax.value = minVal;
        maxVal = minVal;
      }
    }

    // Update text inputs with decimal values; the server formats the French values after refresh.
    this.inputMin.value = Math.round(minVal * 100) / 100;
    this.inputMax.value = Math.round(maxVal * 100) / 100;
    this.#updateTrack();
  };

  #onSliderCommit = (e) => {
    const target = e.target === this.rangeMin ? this.inputMin : this.inputMax;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onTextChange = () => {
    const minVal = this.#parse(this.inputMin.value);
    const maxVal = this.inputMax.value.trim() === '' ? this.#parse(this.rangeMax.max) : this.#parse(this.inputMax.value);
    this.rangeMin.value = minVal;
    this.rangeMax.value = maxVal;
    this.#updateTrack();
  };

  #updateTrack() {
    const min = this.#parse(this.rangeMin.min);
    const max = this.#parse(this.rangeMin.max);
    const minVal = this.#parse(this.rangeMin.value);
    const maxVal = this.#parse(this.rangeMax.value);

    if (max <= min) return;

    const leftPercent = Math.max(0, ((minVal - min) / (max - min)) * 100);
    const rightPercent = Math.max(0, ((max - maxVal) / (max - min)) * 100);

    this.trackFill.style.left = leftPercent + '%';
    this.trackFill.style.right = rightPercent + '%';
  }
}

if (!customElements.get('k9-fullstack-range-slider')) {
  customElements.define('k9-fullstack-range-slider', K9RangeSlider);
}


if (!customElements.get('k9-fullstack-collection-filters')) {
  customElements.define('k9-fullstack-collection-filters', K9FullStackCollectionFilters);
}
